import { useFocusEffect } from 'expo-router';
import { useCallback } from 'react';
import { GQLAnalytics } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useValueEquivalentKeyValuesObject } from 'shared/hooks/useValueEquivalentKeyValuesObject/useValueEquivalentKeyValuesObject';
import { Maybe } from 'shared/utils/types';

export interface AnalyticsProps {
  key: string;
  customAttributes?: Record<string, string> | null;
}

export interface FocusTrackAnalyticsProps {
  name: string;
  customAttributes?: Record<string, unknown> | null;
}

const useViewDidFocusAnalyticsEffect = <TValue extends string, TPayload>(
  value: Maybe<TValue>,
  payload: TPayload,
  logger: (value: TValue, payload: TPayload) => void | Promise<void>,
): void => {
  useFocusEffect(
    useCallback(() => {
      if (value) {
        void logger(value, payload);
      }
    }, [value, payload, logger]),
  );
};

/**
 * Log a screen analytics event returned from gql. This is triggered on screen focus.
 */
export const useViewDidFocusLogScreenAnalytics = (
  props: Maybe<GQLAnalytics>,
): void => {
  const { name, properties } = props || {};
  const customAttributesValue = useValueEquivalentKeyValuesObject(
    properties ?? null,
  );
  const { segmentClient } = useAnalyticsConfiguration();
  const logScreenEvent = useCallback(
    (analyticsName: string, payload: Record<string, unknown>) => {
      return segmentClient.logScreenEvent(analyticsName, payload);
    },
    [segmentClient],
  );

  useViewDidFocusAnalyticsEffect(
    name,
    customAttributesValue ?? {},
    logScreenEvent,
  );
};

/**
 * Log a screen analytics event specified in the config file. This is triggered on screen focus.
 */
export const useViewDidFocusScreenAnalyticsEvent = (
  props: Maybe<AnalyticsProps>,
): void => {
  const { analyticsEvents } = useAnalyticsConfiguration();

  const { key, customAttributes } = props || {};

  const customAttributesValue = useValueEquivalentKeyValuesObject(
    customAttributes ?? null,
  );
  const logScreenEventFromConfig = useCallback(
    (analyticsKey: string, payload: Record<string, string> | null) => {
      return analyticsEvents.logScreenEventFromConfig(analyticsKey, payload);
    },
    [analyticsEvents],
  );

  useViewDidFocusAnalyticsEffect(
    key,
    customAttributesValue ?? null,
    logScreenEventFromConfig,
  );
};

/**
 * Log a track analytics event by name. This is triggered on screen focus.
 */
export const useViewDidFocusTrackAnalyticsEvent = (
  props: Maybe<FocusTrackAnalyticsProps>,
): void => {
  const { segmentClient } = useAnalyticsConfiguration();
  const { name, customAttributes } = props || {};

  const customAttributesValue = useValueEquivalentKeyValuesObject(
    customAttributes ?? null,
  );
  const logEvent = useCallback(
    (eventName: string, payload: Record<string, unknown>) => {
      return segmentClient.logEvent(eventName, payload);
    },
    [segmentClient],
  );

  useViewDidFocusAnalyticsEffect(name, customAttributesValue ?? {}, logEvent);
};
